/**
 * Default values for UK/Ireland livestock farming.
 * Based on Teagasc and AHDB typical values.
 */

import type {
  MonthlyGrazingConfig,
  MonthlySupplementPlan,
  SupplementFeedType,
  SupplementSubstitutionRates,
} from "./types";

// Typical grass growth rates for UK/Ireland (kg DM per ha per day)
export const DEFAULT_GROWTH_RATES: Record<number, number> = {
  0: 0, // Jan
  1: 10, // Feb
  2: 20, // Mar
  3: 30, // Apr
  4: 45, // May
  5: 30, // Jun
  6: 30, // Jul
  7: 25, // Aug
  8: 20, // Sep
  9: 15, // Oct
  10: 10, // Nov
  11: 0, // Dec
};

// Typical grazing days per month
export const DEFAULT_GRAZING_DAYS: Record<number, number> = {
  0: 30, // Jan (some housed)
  1: 28, // Feb
  2: 31, // Mar
  3: 30, // Apr
  4: 31, // May
  5: 30, // Jun
  6: 31, // Jul
  7: 31, // Aug
  8: 30, // Sep
  9: 31, // Oct
  10: 30, // Nov
  11: 31, // Dec
};

// Default utilisation of grass (%)
export const DEFAULT_UTILISATION_PERCENT = 65;

// Default supplement utilisation (%)
export const DEFAULT_SUPPLEMENT_UTILISATION_PERCENT = 65;

export const DEFAULT_SUPPLEMENT_SUBSTITUTION_RATES: SupplementSubstitutionRates =
  {
    silage: 0.89,
    hay: 0.77,
    concentrates: 0.5,
  };

export const CONCENTRATES_FEED_TYPE_ID = "concentrates";

// Housed feeding utilisation defaults
export const DEFAULT_HOUSED_UTILISATION_CONCENTRATE_PERCENT = 95;
export const DEFAULT_HOUSED_UTILISATION_ROUGHAGE_PERCENT = 90;
/** Utilisation % for grass when field grazing (advanced settings) */
export const DEFAULT_GRAZING_FIELD_UTILISATION_PERCENT = 75;
/** Utilisation % for supplements when field feeding (advanced settings) */
export const DEFAULT_FIELD_FEEDING_UTILISATION_PERCENT = 65;

/** Get the default housed utilisation % for a given feed type ID. */
export function getDefaultHousedUtilisation(feedTypeId: string): number {
  return feedTypeId === CONCENTRATES_FEED_TYPE_ID
    ? DEFAULT_HOUSED_UTILISATION_CONCENTRATE_PERCENT
    : DEFAULT_HOUSED_UTILISATION_ROUGHAGE_PERCENT;
}

export const DEFAULT_SUPPLEMENT_FEED_TYPES: SupplementFeedType[] = [
  { id: "hay", name: "Hay", substitutionRate: 0.77 },
  { id: "silage", name: "Silage", substitutionRate: 0.89 },
  {
    id: CONCENTRATES_FEED_TYPE_ID,
    name: "Concentrates",
    substitutionRate: 0.5,
  },
];

/** Look up a substitution rate by common name (lowercase). */
export const COMMON_SUPPLEMENT_RATES: Record<string, number> = {
  hay: 0.77,
  haylage: 0.83,
  silage: 0.89,
  maize: 0.7,
  concentrates: 0.5,
  concentrate: 0.5,
  straw: 0.4,
};

/** Roughage options for the supplement feed type dropdown (excludes concentrates). */
export const ROUGHAGE_SUPPLEMENT_OPTIONS: Array<{
  id: string;
  label: string;
  substitutionRate: number;
}> = [
  { id: "HAY", label: "Hay", substitutionRate: 0.77 },
  { id: "SILAGE", label: "Silage", substitutionRate: 0.89 },
  { id: "HAYLAGE", label: "Haylage", substitutionRate: 0.83 },
  { id: "GRAIN", label: "Grain", substitutionRate: 0.65 },
  { id: "MAIZE", label: "Maize", substitutionRate: 0.7 },
  { id: "STRAW", label: "Straw", substitutionRate: 0.4 },
];

// Default initial pasture cover (kg DM per ha)
export const DEFAULT_INITIAL_PASTURE_COVER = 1800;

// Default target residual (kg DM per ha)
export const DEFAULT_TARGET_RESIDUAL = 1400;

export function createDefaultGrazingMonths(): MonthlyGrazingConfig[] {
  return Array.from({ length: 12 }, (_, month) => ({
    month,
    growthRateKgHaDay: DEFAULT_GROWTH_RATES[month],
    utilisationPercent: DEFAULT_UTILISATION_PERCENT,
  }));
}

export function createDefaultSupplementPlans(): MonthlySupplementPlan[] {
  return Array.from({ length: 12 }, (_, month) => ({
    month,
    utilisationPercent: DEFAULT_SUPPLEMENT_UTILISATION_PERCENT,
    amounts: {},
  }));
}

// Typical allocation (% of body weight) by stock class
export const TYPICAL_ALLOCATIONS: Record<string, number> = {
  DAIRY_COW: 3.0,
  DAIRY_COW_2_KGMS: 3.0,
  SUCKLER_COW: 2.5,
  GROWING_CATTLE: 2.5,
  EWE: 2.0,
  EWE_TWINS: 2.5,
  WEANED_LAMB: 4.0,
};

// Typical weights (kg) by stock class
export const TYPICAL_WEIGHTS: Record<string, number> = {
  DAIRY_COW: 550,
  DAIRY_COW_2_KGMS: 550,
  SUCKLER_COW: 500,
  GROWING_CATTLE: 400,
  EWE: 65,
  EWE_TWINS: 70,
  WEANED_LAMB: 35,
};
